import { useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from 'store';
import { useGetArticleByIdQuery } from 'store/services/blog/blogApi';
import ArticleLayout from 'screens/Auth/Login/Articles/ArticleLayout';
import { Decklist, DecklistProps } from 'components/Decklist';
import { CardLink } from 'components/CardLink';
import { transformCardLinks } from 'utils/cardLinkTransform';
import { AuthorByline } from 'components/AuthorByline';
import Footer from 'components/Footer';
import '../../../screens/Auth/Login/Articles/articles.css';
import '../../Public/DynamicArticle/DynamicArticle.css';
import './ArticlePreviewPage.css';

const ArticlePreviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: article, isLoading, error } = useGetArticleByIdQuery(id || '', {
    skip: !id,
  });
  const contentRef = useRef<HTMLDivElement>(null);
  const decklistRootsRef = useRef<Map<HTMLElement, Root>>(new Map());
  const cardLinkRootsRef = useRef<Map<HTMLElement, Root>>(new Map());

  // Set page title while loading
  useEffect(() => {
    if (article) {
      document.title = `Preview: ${article.title} - TradeMagic Admin`;
    }
  }, [article]);

  // Hydrate decklist components after content renders
  useEffect(() => {
    if (!contentRef.current || !article) return;

    // Copy ref value for cleanup
    const rootsMap = decklistRootsRef.current;

    // Find all decklist placeholder divs
    const decklistElements = contentRef.current.querySelectorAll('[data-type="decklist"]');

    decklistElements.forEach((element) => {
      const htmlElement = element as HTMLElement;

      // Skip if already hydrated
      if (decklistRootsRef.current.has(htmlElement)) return;

      try {
        // Parse decklist data from data attributes
        const title = htmlElement.dataset.title || 'Untitled Deck';
        const author = htmlElement.dataset.author || undefined;
        const format = htmlElement.dataset.format || undefined;
        const decklistId = htmlElement.dataset.id || undefined;

        // Parse mainboard
        let mainboard: DecklistProps['mainboard'] = [];
        if (htmlElement.dataset.mainboard) {
          try {
            mainboard = JSON.parse(htmlElement.dataset.mainboard);
          } catch (e) {
            console.error('Failed to parse mainboard data:', e);
          }
        }

        // Parse sideboard
        let sideboard: DecklistProps['sideboard'] = [];
        if (htmlElement.dataset.sideboard) {
          try {
            sideboard = JSON.parse(htmlElement.dataset.sideboard);
          } catch (e) {
            console.error('Failed to parse sideboard data:', e);
          }
        }

        // Create React root and render Decklist component
        const root = createRoot(htmlElement);
        decklistRootsRef.current.set(htmlElement, root);

        root.render(
          <Provider store={store}>
            <Decklist
              title={title}
              author={author}
              format={format}
              mainboard={mainboard}
              sideboard={sideboard}
              id={decklistId}
            />
          </Provider>
        );
      } catch (err) {
        console.error('Failed to hydrate decklist:', err);
      }
    });

    // Cleanup function to unmount React roots
    return () => {
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
    };
  }, [article]);

  // Hydrate card link components after content renders
  useEffect(() => {
    if (!contentRef.current || !article) return;

    // Copy ref value for cleanup
    const rootsMap = cardLinkRootsRef.current;

    // Find all card link placeholder spans
    const cardLinkElements = contentRef.current.querySelectorAll('.card-link-placeholder[data-card-name]');

    cardLinkElements.forEach((element) => {
      const htmlElement = element as HTMLElement;

      // Skip if already hydrated
      if (cardLinkRootsRef.current.has(htmlElement)) return;

      try {
        const cardName = htmlElement.dataset.cardName;
        if (!cardName) return;

        // Create React root and render CardLink component
        const root = createRoot(htmlElement);
        cardLinkRootsRef.current.set(htmlElement, root);

        root.render(
          <Provider store={store}>
            <CardLink cardName={cardName}>
              {cardName}
            </CardLink>
          </Provider>
        );
      } catch (err) {
        console.error('Failed to hydrate card link:', err);
      }
    });

    // Cleanup function to unmount React roots
    return () => {
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
    };
  }, [article]);

  // Loading state
  if (isLoading) {
    return (
      <div className="dynamic-article-loading">
        <div className="spinner-large"></div>
        <p>Loading article preview...</p>
      </div>
    );
  }

  // Error / Not found state
  if (error || !article) {
    return <Navigate to="/admin/blog/articles" replace />;
  }

  // NOTE: Unlike DynamicArticle, we do NOT check article.status here
  // This allows admins to preview draft and archived articles

  const formatPublishedDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return 'Draft';
      case 'PUBLISHED':
        return 'Published';
      case 'ARCHIVED':
        return 'Archived';
      default:
        return status;
    }
  };

  return (
    <>
      {/* Preview Mode Banner */}
      <div className="preview-mode-banner">
        <div className="preview-mode-info">
          <span className="preview-mode-label">Preview Mode</span>
          <span className={`preview-status-badge preview-status-${article.status.toLowerCase()}`}>
            {getStatusLabel(article.status)}
          </span>
        </div>
        <div className="preview-mode-actions">
          <button
            className="preview-edit-button"
            onClick={() => navigate(`/admin/blog/articles/${id}/edit`)}
          >
            Edit
          </button>
          <button
            className="preview-exit-button"
            onClick={() => navigate('/admin/blog/articles')}
          >
            Exit Preview
          </button>
        </div>
      </div>

      {/* Article Layout - No SEO components for preview */}
      <div className="article-preview-container">
        <ArticleLayout
          title={article.title}
          hero={article.heroImageUrl || ''}
        >
          <article className="dynamic-article-wrapper">
            {/* Subtitle */}
            {article.subtitle && (
              <p className="article-subtitle-text">{article.subtitle}</p>
            )}

            {/* Author byline with date, or fallback to date only */}
            {article.author ? (
              <AuthorByline author={article.author} publishedDate={article.publishedAt} />
            ) : (
              <p className="article-published-date">
                {article.publishedAt
                  ? `Published on ${formatPublishedDate(article.publishedAt)}`
                  : 'Not yet published'}
              </p>
            )}

            {/* Article content (HTML from TipTap) */}
            <div
              ref={contentRef}
              className="article-content"
              dangerouslySetInnerHTML={{ __html: transformCardLinks(article.contentHtml) }}
            />
          </article>

          <Footer />
        </ArticleLayout>
      </div>
    </>
  );
};

export default ArticlePreviewPage;
